import ListPageController, { type StatCardDef } from "./mixins/ListPageController";
import JSONModel from "sap/ui/model/json/JSONModel";
import type { Select$ChangeEvent } from "sap/m/Select";
import type { SearchField$LiveChangeEvent, SearchField$SearchEvent } from "sap/m/SearchField";

type StatusKey = "ALL" | "Active" | "Pending" | "Suspended" | "Cancelled" | "Expired";
type PaymentKey = "ALL" | "Paid" | "GracePeriod" | "PendingPayment" | "Overdue" | "NotRequired";

interface ViewState {
	statusFilter: StatusKey;
	paymentFilter: PaymentKey;
	searchQuery: string;
}

interface RowBindingContext {
	getProperty(p: string): unknown;
}

interface RowLinkEvent {
	getSource(): { getBindingContext(name: string): RowBindingContext | null };
}

interface VariantPreset {
	status: StatusKey;
	payment: PaymentKey;
}

const VARIANTS: Record<string, VariantPreset> = {
	"standard":  { status: "ALL",       payment: "ALL" },
	"active":    { status: "Active",    payment: "ALL" },
	"pending":   { status: "Pending",   payment: "ALL" },
	"grace":     { status: "Active",    payment: "GracePeriod" },
	"overdue":   { status: "ALL",       payment: "Overdue" },
	"suspended": { status: "Suspended", payment: "ALL" }
};

// Karty statystyk nad tabelą — ten sam fragment StatCardsRow co Organizations/Dashboard.
const STATS: readonly StatCardDef[] = [
	{ key: "total", labelKey: "entitlements.stats.total.label", subtitleKey: "entitlements.stats.total.subtitle", state: "Neutral" },
	{ key: "active", labelKey: "entitlements.stats.active.label", subtitleKey: "entitlements.stats.active.subtitle", state: "Good", filter: "Status eq 'Active'" },
	{ key: "suspended", labelKey: "entitlements.stats.suspended.label", subtitleKey: "entitlements.stats.suspended.subtitle", state: "Critical", filter: "Status eq 'Suspended'" },
	{ key: "overdue", labelKey: "entitlements.stats.overdue.label", subtitleKey: "entitlements.stats.overdue.subtitle", state: "Error", filter: "PaymentState eq 'Overdue'" }
];

/**
 * @namespace computec.b2b.portal.controller
 */
export default class Entitlements extends ListPageController {
	public override onInit(): void {
		const initial: ViewState = { statusFilter: "ALL", paymentFilter: "ALL", searchQuery: "" };
		this.getView().setModel(new JSONModel(initial), "view");
		void this.initStatsCards("EntitlementReports", STATS);
		this.getRouter().getRoute("entitlements")?.attachPatternMatched(() => { void this.onShow(); });
	}

	private async onShow(): Promise<void> {
		const bundle = await this.getResourceBundle();
		const title = bundle.getText("entitlements.title") ?? "Entitlements";
		this.setPageHeader({ title, subtitle: bundle.getText("entitlements.subtitle") ?? "" });
		this.applyFilters();
		void this.refreshStatsCards();
	}

	public onSearch(event: SearchField$SearchEvent | SearchField$LiveChangeEvent): void {
		const params = event.getParameters() as { query?: string; newValue?: string };
		const raw = params.query ?? params.newValue ?? "";
		(this.getView().getModel("view") as JSONModel).setProperty("/searchQuery", raw);
		this.applyFilters();
	}

	public onStatusSelect(event: Select$ChangeEvent): void {
		const key = (event.getParameter("selectedItem")?.getKey() ?? "ALL") as StatusKey;
		(this.getView().getModel("view") as JSONModel).setProperty("/statusFilter", key);
		this.applyFilters();
	}

	public onPaymentSelect(event: Select$ChangeEvent): void {
		const key = (event.getParameter("selectedItem")?.getKey() ?? "ALL") as PaymentKey;
		(this.getView().getModel("view") as JSONModel).setProperty("/paymentFilter", key);
		this.applyFilters();
	}

	public onVariantSelect(event: Select$ChangeEvent): void {
		const key = event.getParameter("selectedItem")?.getKey() ?? "standard";
		const preset = VARIANTS[key];
		if (!preset) {
			return;
		}
		const model = this.getView().getModel("view") as JSONModel;
		model.setProperty("/statusFilter", preset.status);
		model.setProperty("/paymentFilter", preset.payment);
		model.setProperty("/searchQuery", "");
		this.applyFilters();
	}

	public onRefresh(): void {
		this.refreshTable("entitlementTable");
		void this.refreshStatsCards();
	}

	public onOpenEntitlement(event: RowLinkEvent): void {
		this.navFromRow(event, "Id", "entitlementDetail");
	}

	public onOpenPartner(event: RowLinkEvent): void {
		this.navToOrganizationFromRow(event, "PartnerOrgId");
	}

	public onOpenCustomer(event: RowLinkEvent): void {
		this.navToOrganizationFromRow(event, "OwnerOrgId");
	}

	public onOpenProduct(event: RowLinkEvent): void {
		this.navFromRow(event, "ProductId", "productDetail");
	}

	public onOpenFamily(event: RowLinkEvent): void {
		this.navFromRow(event, "ProductFamilyId", "productFamilyDetail");
	}

	private navFromRow(event: RowLinkEvent, idProperty: string, route: string): void {
		const id = this.rowId(event, idProperty);
		if (id) {
			this.navTo(route, { id });
		}
	}

	private navToOrganizationFromRow(event: RowLinkEvent, idProperty: string): void {
		const id = this.rowId(event, idProperty);
		if (id) {
			this.navToOrganization(id);
		}
	}

	private rowId(event: RowLinkEvent, idProperty: string): string | undefined {
		const ctx = event.getSource().getBindingContext("odata");
		return ctx ? ctx.getProperty(idProperty) as string | undefined : undefined;
	}

	private applyFilters(): void {
		const state = (this.getView().getModel("view") as JSONModel).getData() as ViewState;
		const parts: string[] = [];

		if (state.statusFilter !== "ALL") {
			parts.push(`Status eq '${state.statusFilter}'`);
		}
		if (state.paymentFilter !== "ALL") {
			parts.push(`PaymentState eq '${state.paymentFilter}'`);
		}
		const search = state.searchQuery.trim();
		if (search) {
			const q = escapeODataString(search.toLowerCase());
			parts.push(`(contains(tolower(ProductName),'${q}') or contains(tolower(ProductCode),'${q}') or contains(tolower(OwnerOrgName),'${q}'))`);
		}

		const binding = this.getTableBinding("entitlementTable");
		binding?.changeParameters({ $filter: parts.length > 0 ? parts.join(" and ") : undefined });
	}
}

function escapeODataString(value: string): string {
	return value.replace(/'/g, "''");
}
